import { z } from 'zod';

import { CustomerRefSchema, PersonRefSchema, RoleRefSchema } from './external-refs.js';

/**
 * Aggregate 1 — the project plan.
 *
 * Spec: docs/specs/domain-model.md (Aggregate 1) and docs/specs/business-rules.md.
 * Only base data is stored; every number (rollups, consumption, percentages) is
 * derived on load by calculations.ts. Realized hours are NEVER stored here — they
 * join from synced actuals at read time (BR-22).
 *
 * Scale contract: a plan is ONE document that may hold tens of thousands of
 * tasks at unbounded nesting depth (BR-3). Everything in this module therefore
 * validates and traverses ITERATIVELY — no recursion anywhere, so the only depth
 * limits are the platform's (native JSON parse/stringify and Postgres jsonb,
 * both in the thousands of levels).
 */

/** v1 was the scaffold placeholder (phases/workItems). v2 is the real plan model. */
export const CURRENT_DOCUMENT_SCHEMA_VERSION = 2;

/**
 * Maximum plan depth in levels, counting deliverables (BR-23). Deliverables map
 * to TLP root tasks, so 5 levels here ≡ TLP's five WBS levels (MAXWBSLEVEL = 5).
 * This is a validation-level ceiling only — every algorithm in this module stays
 * depth-independent (BR-3), so lifting the limit is a one-constant change.
 */
export const MAX_PLAN_DEPTH = 5;

export const PlanNodeStatusSchema = z.enum(['planned', 'active', 'done']);
export type PlanNodeStatus = z.infer<typeof PlanNodeStatusSchema>;

/**
 * A staffing line: one role, optionally one person, and hours. The role is
 * mandatory (D16) — there is no "unset" sentinel; a line that names nobody
 * cannot be saved. The person's role may differ from the line's: picking a
 * person after a role keeps the role, picking the person first only suggests
 * their default role.
 */
export const AllocationSchema = z.object({
  id: z.string().uuid(),
  roleRef: RoleRefSchema,
  /** Absent = role-only allocation ("Any developer"). */
  personRef: PersonRefSchema.optional(),
  hours: z.number().min(0).max(100_000).multipleOf(0.01),
});
export type Allocation = z.infer<typeof AllocationSchema>;

/*
  There is no "resolved vs draft" predicate on a stored line any more: a line
  without a role never reaches the document (BR-29, ADR 0032 → role identity),
  so every Allocation here is a plan. The editor keeps its own DraftLine for the
  moment between "Add resource" and the role being picked.
*/

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date (YYYY-MM-DD)');

/**
 * One node's own fields — deliberately NON-recursive. The tree structure is
 * validated iteratively by the document transform below; this schema is what
 * each node is checked against.
 */
const planNodeFieldsSchema = z.object({
  id: z.string().uuid(),
  /** 'D-1' / 'T-101'. Assigned once by the server from codeCounters (BR-6); optional on input. */
  code: z.string().min(1).max(16).optional(),
  kind: z.enum(['deliverable', 'task']),
  name: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  status: PlanNodeStatusSchema.default('planned'),
  startDate: isoDate.optional(),
  endDate: isoDate.optional(),
  targetDate: isoDate.optional(),
  /** Tasks only. Default true; false ⇒ consumes hours budget, contributes 0 revenue (BR-5). */
  billable: z.boolean().optional(),
  /**
   * The contract this leaf is mapped to (BR-4). Explicit only: absent means the
   * task has no contract and no price — there is no fallback to a project
   * default, and no inheritance from a parent (D8).
   */
  contractId: z.string().uuid().optional(),
  /** Default pricing role for single-estimate leaves. */
  roleRef: RoleRefSchema.optional(),
  assignee: PersonRefSchema.optional(),
  /**
   * The leaf's BUDGET — what the work is worth (CALC-1). Leaf tasks only
   * (BR-1); independent of `allocations` (BR-28), which it is compared against
   * and never reconciled with.
   */
  estimatedHours: z.number().min(0).max(1_000_000).multipleOf(0.01).optional(),
  /**
   * Who is committed to the leaf, and for how long each. Leaf tasks only
   * (BR-1). Independent of the budget (BR-28): allocating never rewrites it,
   * and the two may legitimately disagree.
   */
  allocations: z.array(AllocationSchema).default([]),
});

export type PlanNode = z.infer<typeof planNodeFieldsSchema> & { children: PlanNode[] };

/**
 * Descriptive recursive schema for OpenAPI/typing ONLY. Its `.parse` would
 * recurse and hit the call stack around depth ~1,000 — never use it to validate
 * data. Runtime validation goes through ProjectDocumentSchema, which walks the
 * tree iteratively.
 */
export const PlanNodeShapeSchema: z.ZodType<PlanNode> = z.lazy(() =>
  planNodeFieldsSchema.extend({
    children: z.array(PlanNodeShapeSchema).default([]),
  }),
) as unknown as z.ZodType<PlanNode>;

const projectDocumentBaseSchema = z.object({
  schemaVersion: z.literal(CURRENT_DOCUMENT_SCHEMA_VERSION),
  name: z.string().min(1).max(200),
  /** TimeLog project number, e.g. "P26.0007". */
  code: z.string().max(50).optional(),
  description: z.string().max(4000).optional(),
  customer: CustomerRefSchema.optional(),
  currency: z.string().length(3).default('DKK'),
  /**
   * The contract prefilled on tasks created from now on (BR-4, D8). A creation-time
   * default only — it never resolves anything at read time, so changing it moves
   * no existing task. Must reference a contract of this project (BR-7).
   */
  defaultContractId: z.string().uuid().optional(),
  /**
   * Blended internal cost rate (currency/h). Two jobs remain (D14): the seed for
   * Own Work hours before the user types them (CALC-11), and the cost of hours
   * that no person, contract or rate card prices — an unmapped leaf, or a T&M
   * role without a cost rate (CALC-21).
   */
  blendedCostRate: z.number().min(1).max(100_000).default(650),
  /** Monotonic counters backing stable node codes (BR-6). Task codes start at T-101. */
  codeCounters: z
    .object({ deliverable: z.number().int().min(0), task: z.number().int().min(0) })
    .default({ deliverable: 0, task: 100 }),
  /** PlanNode[] — validated iteratively; nesting depth is unbounded (BR-3). */
  plan: z
    .array(z.unknown())
    .default([])
    .describe('Array of PlanNode (recursive; see the response schema for the full shape)'),
});

/** Lazy path segments so deep trees never pay for path arrays unless an error occurs. */
type PathLink = { parent: PathLink | null; segments: (string | number)[] };

const materializePath = (link: PathLink): (string | number)[] => {
  const chain: PathLink[] = [];
  for (let current: PathLink | null = link; current; current = current.parent) chain.push(current);
  chain.reverse();
  return chain.flatMap((entry) => entry.segments);
};

/**
 * Validation modes (ADR 0023, PM-2025).
 *
 * 'strict' is the API contract: BR-1/BR-2/BR-23 as specified. 'bridge' is the
 * coexistence write path from the relational work-item core, where ADR 0023
 * retired BR-1/BR-2 and allows task depth 1–5 beneath a project or delivery:
 * a bridged plan may root tasks directly under the project, carry direct
 * budgets on parents/deliverables (budget_rollup_state, M4), and reach one
 * level deeper (deliverable + 5 task levels). Everything else — shapes, ids,
 * dates, the depth ceiling itself — stays enforced.
 */
type PlanValidationMode = 'strict' | 'bridge';

/**
 * Iterative (explicit-stack) validation of the whole plan tree: node shapes,
 * BR-1 (leaf-only estimates/allocations), BR-2 (deliverables as roots only),
 * duplicate-id rejection, and defaults application — in one pass, at any depth.
 */
const validatePlan = (
  rawPlan: unknown[],
  ctx: z.RefinementCtx,
  mode: PlanValidationMode = 'strict',
): PlanNode[] => {
  const roots: PlanNode[] = [];
  const seenIds = new Set<string>();

  const issue = (path: PathLink, message: string): void => {
    ctx.addIssue({ code: 'custom', path: materializePath(path), message });
  };

  type Frame = { raw: unknown; path: PathLink; depth: number; out: PlanNode[] };
  const stack: Frame[] = [];
  for (let i = rawPlan.length - 1; i >= 0; i--) {
    stack.push({
      raw: rawPlan[i],
      path: { parent: null, segments: ['plan', i] },
      depth: 0,
      out: roots,
    });
  }

  while (stack.length > 0) {
    const frame = stack.pop()!;
    const { raw, path, depth, out } = frame;

    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      issue(path, 'Expected a plan node object');
      continue;
    }

    // BR-23 — one error at the first offending level, not one per descendant.
    // Bridge mode allows one extra level: deliverable + 5 task levels (ADR 0023).
    const maxDepth = mode === 'bridge' ? MAX_PLAN_DEPTH + 1 : MAX_PLAN_DEPTH;
    if (depth >= maxDepth) {
      issue(path, `Plans are limited to ${maxDepth} levels including deliverables (BR-23)`);
      continue;
    }

    const { children: rawChildren, ...fields } = raw as Record<string, unknown>;
    const parsed = planNodeFieldsSchema.safeParse(fields);
    if (!parsed.success) {
      for (const nodeIssue of parsed.error.issues) {
        ctx.addIssue({
          code: 'custom',
          path: [...materializePath(path), ...nodeIssue.path] as (string | number)[],
          message: nodeIssue.message,
        });
      }
      continue;
    }

    const children = rawChildren === undefined ? [] : rawChildren;
    if (!Array.isArray(children)) {
      issue({ parent: path, segments: ['children'] }, 'Expected an array of plan nodes');
      continue;
    }

    const node = parsed.data as PlanNode;

    if (seenIds.has(node.id)) {
      issue({ parent: path, segments: ['id'] }, `Duplicate plan node id '${node.id}'`);
    }
    seenIds.add(node.id);

    // BR-2 — deliverables are roots and carry no work/billing fields of their own.
    // Retired for bridged plans (ADR 0023): rp trees may root tasks directly
    // under the project and carry direct budgets on deliverables/parents.
    if (mode === 'strict' && depth === 0 && node.kind !== 'deliverable') {
      issue({ parent: path, segments: ['kind'] }, 'Root plan nodes must be deliverables (BR-2)');
    }
    if (depth > 0 && node.kind !== 'task') {
      issue({ parent: path, segments: ['kind'] }, 'Nested plan nodes must be tasks (BR-2)');
    }
    if (mode === 'strict' && node.kind === 'deliverable' && children.length > 0) {
      /*
        The billing-field ban applies to a root that HAS children (ADR 0028,
        widened): a childless root node is a leaf on screen and a leaf to
        CALC-1, so it may carry its own estimate — and, now that a contract is
        an explicit mapping with no project-level fallback (D8), its own
        contract, role and assignee too, or its hours could never be priced.
        BR-2's "they aggregate" only bites once there are children to
        aggregate; BR-1's check below still rejects an estimate on any node
        with children, deliverable or task.
      */
      for (const field of ['contractId', 'roleRef', 'assignee', 'billable'] as const) {
        if (node[field] !== undefined) {
          issue(
            { parent: path, segments: [field] },
            `Deliverables aggregate their tasks and cannot carry '${field}' (BR-2)`,
          );
        }
      }
    }
    /*
      There is deliberately NO allocations clause here any more (ADR 0036).
      It used to refuse them on a root row "regardless of children", which read
      as a rule but was a loop: the ban's stated reason was that the Resources
      overlay is not offered on those rows, while the overlay was withheld
      because the schema refused the save. A plan may legitimately consist of
      nothing but standalone top-level tasks, and those have to be staffable.

      Nothing is lost by dropping it: BR-1 immediately below refuses allocations
      on ANY node with children, deliverable or task, and says so in better
      words. This is the same widening ADR 0033 applied to `contractId`,
      `roleRef`, `assignee` and `billable` just above — allocations were the one
      field it stopped short of.
    */

    // BR-1 — estimates and allocations live on leaves only. Retired for
    // bridged plans (ADR 0023): a parent work item may carry a DIRECT budget
    // until the rollup engine (M4) reconciles it.
    if (mode === 'strict' && children.length > 0) {
      if (node.estimatedHours !== undefined) {
        issue(
          { parent: path, segments: ['estimatedHours'] },
          'A node with children cannot carry its own estimate — move the hours onto leaves (BR-1)',
        );
      }
      if (node.allocations.length > 0) {
        issue(
          { parent: path, segments: ['allocations'] },
          'A node with children cannot carry allocations — move them onto leaves (BR-1)',
        );
      }
    }
    /*
      A leaf carrying BOTH a budget and allocations is VALID (BR-28, ADR 0030).
      They are two independent quantities: the budget is what the task is worth
      doing, the allocations are who is committed to it, and the two are
      compared — never reconciled. This used to be rejected on the strength of
      finding F22 ("stale hours retained under allocations"); F22 is re-resolved
      by showing both numbers instead of destroying one. The resource dimension
      has worked this way since ADR 0023 (DL-004); this is the financial
      document catching up.
    */

    node.children = [];
    out.push(node);

    for (let i = children.length - 1; i >= 0; i--) {
      stack.push({
        raw: children[i],
        path: { parent: path, segments: ['children', i] },
        depth: depth + 1,
        out: node.children,
      });
    }
  }

  return roots;
};

export const ProjectDocumentSchema = projectDocumentBaseSchema.transform(
  (
    document,
    ctx,
  ): Omit<z.infer<typeof projectDocumentBaseSchema>, 'plan'> & { plan: PlanNode[] } => ({
    ...document,
    plan: validatePlan(document.plan, ctx),
  }),
);

export type ProjectDocument = z.infer<typeof ProjectDocumentSchema>;

/**
 * The coexistence write validator (ADR 0021/0023, PM-2025): documents
 * regenerated FROM the relational work-item core. Same iterative validation,
 * with the ADR 0023 relaxations ('bridge' mode above). Never used for API
 * input — client writes always go through the strict ProjectDocumentSchema.
 */
export const BridgedProjectDocumentSchema = projectDocumentBaseSchema.transform(
  (
    document,
    ctx,
  ): Omit<z.infer<typeof projectDocumentBaseSchema>, 'plan'> & { plan: PlanNode[] } => ({
    ...document,
    plan: validatePlan(document.plan, ctx, 'bridge'),
  }),
);

/**
 * Descriptive document schema for OpenAPI responses. Structurally identical to
 * ProjectDocumentSchema's output but built from the recursive shape schema, so
 * generated API docs show the real PlanNode tree. Never used to parse data.
 */
export const ProjectDocumentShapeSchema = projectDocumentBaseSchema.extend({
  plan: z.array(PlanNodeShapeSchema).default([]),
});

/** A new project starts with an empty plan. */
export const createEmptyProjectDocument = (
  name: string,
  overrides: Partial<
    Pick<ProjectDocument, 'code' | 'description' | 'customer' | 'currency' | 'blendedCostRate'>
  > = {},
): ProjectDocument =>
  ProjectDocumentSchema.parse({
    schemaVersion: CURRENT_DOCUMENT_SCHEMA_VERSION,
    name,
    ...overrides,
  });

/** Iterative depth-first (pre-order) walk over every node in the plan. */
export const walkPlan = (
  nodes: PlanNode[],
  visit: (node: PlanNode, depth: number, parent: PlanNode | undefined) => void,
): void => {
  type Frame = { node: PlanNode; depth: number; parent: PlanNode | undefined };
  const stack: Frame[] = [];
  for (let i = nodes.length - 1; i >= 0; i--) {
    stack.push({ node: nodes[i]!, depth: 0, parent: undefined });
  }
  while (stack.length > 0) {
    const { node, depth, parent } = stack.pop()!;
    visit(node, depth, parent);
    for (let i = node.children.length - 1; i >= 0; i--) {
      stack.push({ node: node.children[i]!, depth: depth + 1, parent: node });
    }
  }
};

/** All leaf nodes (the only carriers of hours and allocations). */
type RewriteFrame = {
  node: PlanNode;
  childIndex: number;
  newChildren: PlanNode[];
  changed: boolean;
  parent: RewriteFrame | undefined;
};

/**
 * Iterative post-order rewrite with structural sharing: `rewriteNode` returns
 * either the original node (no change) or a shallow-patched copy; parents are
 * only copied when something underneath them actually changed. Plans can hold
 * tens of thousands of nodes at unbounded depth (BR-3), so nothing recurses.
 * Shared by the server-side plan rules, the contract-mapping transforms and
 * the migration tooling.
 */
export const rewritePlan = (
  plan: PlanNode[],
  rewriteNode: (node: PlanNode) => PlanNode,
): { plan: PlanNode[]; changed: boolean } => {
  const newRoots: PlanNode[] = [];
  let rootsChanged = false;

  for (const root of plan) {
    const stack: RewriteFrame[] = [
      { node: root, childIndex: 0, newChildren: [], changed: false, parent: undefined },
    ];

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;

      if (frame.childIndex < frame.node.children.length) {
        const child = frame.node.children[frame.childIndex++]!;
        stack.push({ node: child, childIndex: 0, newChildren: [], changed: false, parent: frame });
        continue;
      }

      stack.pop();
      const rewritten = rewriteNode(frame.node);
      const selfChanged = rewritten !== frame.node;
      const result =
        frame.changed || selfChanged
          ? { ...rewritten, children: frame.changed ? frame.newChildren : rewritten.children }
          : frame.node;
      const changed = frame.changed || selfChanged;

      if (frame.parent) {
        frame.parent.newChildren.push(result);
        frame.parent.changed ||= changed;
      } else {
        newRoots.push(result);
        rootsChanged ||= changed;
      }
    }
  }

  return { plan: rootsChanged ? newRoots : plan, changed: rootsChanged };
};

export const collectLeaves = (nodes: PlanNode[]): PlanNode[] => {
  const leaves: PlanNode[] = [];
  walkPlan(nodes, (node) => {
    if (node.children.length === 0) leaves.push(node);
  });
  return leaves;
};
